// Theme preference: light, dark or system. Persisted in localStorage and applied to <html>
// before first paint (apps inline `themeBootScript` in their index.html <head>).
//
// What gets applied:
// - `data-theme="light" | "dark"` for an explicit choice (absent for "system", so the CSS
//   `prefers-color-scheme` block decides);
// - `class="dark"` whenever the resolved theme is dark (system or explicit), which drives the
//   `dark:` variant and chart.tsx's `.dark` selector.
import { useCallback, useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "companyos.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * Self-contained (no closures, no imports) so its source can be inlined as the boot script.
 * Returns the resolved theme.
 */
function bootTheme(key: string, forced?: string): "light" | "dark" {
  let pref = "system";
  if (forced === "light" || forced === "dark" || forced === "system") pref = forced;
  else {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === "light" || stored === "dark") pref = stored;
    } catch {
      // storage blocked: fall back to the system preference
    }
  }
  const systemDark =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = pref === "dark" || (pref === "system" && systemDark);
  const root = document.documentElement;
  if (pref === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", pref);
  root.classList.toggle("dark", dark);
  return dark ? "dark" : "light";
}

/** Inline in `<head>` before any stylesheet so the first paint already has the right theme. */
export const themeBootScript = `(${bootTheme.toString()})(${JSON.stringify(THEME_STORAGE_KEY)});`;

/**
 * The preference chosen in this page. It is the source of truth once set, so a choice still
 * holds when storage is blocked (private mode, disabled site data); storage is only a cache
 * that survives the reload.
 */
let memoryPreference: ThemePreference | null = null;

export function readThemePreference(): ThemePreference {
  if (memoryPreference) return memoryPreference;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // storage blocked: system until the user chooses
  }
  return "system";
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (pref === "system") return systemDark ? "dark" : "light";
  return pref;
}

/** Apply a preference to <html> without persisting it. */
export function applyTheme(pref: ThemePreference): ResolvedTheme {
  return bootTheme(THEME_STORAGE_KEY, pref);
}

const listeners = new Set<() => void>();
let stopWatching: (() => void) | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Follows OS theme changes for the "system" preference. Call once at app startup (it is
 * idempotent), so screens without a theme control, such as sign-in, still track the OS.
 * Returns a function that stops listening.
 */
export function startThemeSync(): () => void {
  if (stopWatching) return stopWatching;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (readThemePreference() === "system") applyTheme("system");
    notify();
  };
  query.addEventListener("change", onChange);
  stopWatching = () => {
    query.removeEventListener("change", onChange);
    stopWatching = null;
  };
  return stopWatching;
}

/** Persist and apply a preference; it holds in memory even when storage is blocked. */
export function setThemePreference(pref: ThemePreference): void {
  memoryPreference = pref;
  try {
    if (pref === "system") window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // storage blocked: the choice lasts for this page only
  }
  applyTheme(pref);
  notify();
}

function subscribe(listener: () => void): () => void {
  startThemeSync();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): string {
  const pref = readThemePreference();
  const resolved = document.documentElement.classList.contains("dark") ? "dark" : "light";
  return `${pref}:${resolved}`;
}

/** React binding: the stored preference, the resolved theme, and a setter. */
export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
} {
  const value = useSyncExternalStore(subscribe, snapshot, () => "system:light");
  const [preference, resolved] = value.split(":") as [ThemePreference, ResolvedTheme];
  const setPreference = useCallback((pref: ThemePreference) => setThemePreference(pref), []);
  return { preference, resolved, setPreference };
}
